import React, {FC, SyntheticEvent, useCallback, useEffect, useRef, useState} from 'react';
import './TaskTimer.less';
import FinancialHelper from '@/Financial/FinancialHelper';
import IconButton from '@/ui/IconButton/IconButton';
import Icons from '@/ui/IconButton/Icons';
import DurationEntry from '@/ui/DurationEntry/DurationEntry';
import Checkbox from '@/ui/Checkbox';
import * as DateUtil from '@/lib/dateutils';
import Task from '@/entities/task';
import * as TaskHelper from '../TaskHelper';
import Auth from '@/Auth/Auth';
import {getCurrentUserId} from "@/lib/picos/currentLogin";
import {formatDuration} from "@/lib/picos/duration";
import DatePicker from "@/ui/DateComponents/DatePicker/DatePicker";

/**
 * Widget to show buttons to start/pause, edit/cancel/accept, and complete/un-complete a task.
 *
 * @constructor
 */
const TaskTimer: FC<TaskTimerProps> = (props) => {
    // Use the props to set up the initial state
    const [isEditingTimer, setIsEditingTimer] = useState<boolean>(false);
    const [durationValue, setDurationValue] = useState<number>(props.task.elapsedTime);

    const runningIntervalRef = useRef<NodeJS.Timeout>(null);

    // Sync durationValue with props.task.elapsedTime when not running and not editing
    // This ensures that when the parent updates elapsedTime (e.g., after a manual edit is saved),
    // the displayed value reflects the saved value. (OWA-336)
    // Note: queueMicrotask defers the setState to avoid synchronous state updates within effects
    useEffect(() => {
        if (!props.task.running && !isEditingTimer) {
            queueMicrotask(() => setDurationValue(props.task.elapsedTime));
        }
    }, [props.task.elapsedTime, props.task.running, isEditingTimer]);

    useEffect(() => {
        // If we are running but the interval isn't, start it up.
        if (props.task.running && !runningIntervalRef.current) {
            runningIntervalRef.current = setInterval(() => {
                setDurationValue(props.task.elapsedTime + new Date().getTime() - props.task.lastStart);
            }, 1000);
        }

        // If we are not running but the interval is, stop it.
        if (!props.task.running && runningIntervalRef.current) {
            clearInterval(runningIntervalRef.current);
            runningIntervalRef.current = null;
        }

        return () => {
            // Always quit the interval when we are done.
            if (runningIntervalRef.current) {
                clearInterval(runningIntervalRef.current);
                runningIntervalRef.current = null;
            }
        }
    }, [props.task.elapsedTime, props.task.id, props.task.lastStart, props.task.running, setDurationValue]);

    /**
     * Called when the timer is started.
     *
     * props.onStartTimer will do a save of the task, so we don't need to do that here.
     * That save will trigger a reload of this component, which will start the interval timer.
     *
     * @param {Event} e
     * @private
     */
    const onStartTimer = useCallback((e: SyntheticEvent) => {
        props.onStartTimer(e);
    }, [props]);

    /**
     * Called when the timer is stopped.
     *
     * @param {Event} e
     * @private
     */
    const onPauseTimer = useCallback((e: SyntheticEvent) => {
        props.onPauseTimer(e);
    }, [props]);

    /**
     * Called when the value changes in DurationEntry.
     *
     * @private
     */
    const onAcceptTimerValue = useCallback(() => {
        setIsEditingTimer(false);
        props.onAcceptTimerValue(durationValue);
        props.onEditStateChange(false);
    }, [props, durationValue]);

    /**
     * Called when we start editing the DurationEntry using the PLAY button.
     *
     * @private
     */
    const onEditTimer = useCallback(() => {
        setIsEditingTimer(true);

        props.onEditStateChange(true);
    }, [props, setIsEditingTimer]);

    /**
     * Called when we cancel editing of the DurationEntry using the CANCEL button.
     *
     * @private
     */
    const onCancelEditTimer = useCallback(() => {
        setIsEditingTimer(false);
        setDurationValue(props.task.elapsedTime);

        props.onEditStateChange(false);
    }, [props, setIsEditingTimer]);

    /**
     * Called when the value in the DurationEntry changes.
     *
     * @param {number} value
     * @private
     */
    const onChangeTimerValue = useCallback((value: number) => {
        setDurationValue(value);
    }, [setDurationValue]);

    /**
     * Method to render timer's numeric value to text.
     *
     * @returns {string}
     * @private
     */
    const renderTimerText = useCallback(() => {
        return formatDuration(durationValue || 0, 'hms');
    }, [durationValue]);

    // Get task and callbacks from props
    const {
        task, onCompletionToggled
    } = props;

    // Is this task assigned to a group (rather than a user)?
    const isGroupAssigned = task.assignedToType === 'G';

    // We do not allow editing if the task is included in a finalized invoice
    const finalized = task.invoiceId ? FinancialHelper.getFinalizedByInvoiceId(task.invoiceId) : false;

    // Can this task be started by the current user?
    const canStart = TaskHelper.amIAssigned(task) && task.assignedToType === 'U';

    // Am I an admin?  This lets me edit a timer that is not my own.
    const amIAdmin = Auth.isAdmin(getCurrentUserId());

    // For performance reasons, we create the OK/Cancel buttons together
    const CHECK = <>
        <IconButton
            id={`${task.id}-cancel-edit`}
            type={Icons.CLOSE}
            size={Icons.IconSizeNormal}
            title={'Cancel Edit'}
            onClick={onCancelEditTimer}
            useDelay={false}
        />
        <IconButton
            id={`${task.id}-edit-timer`}
            type={Icons.CHECKMARK}
            size={Icons.IconSizeNormal}
            title={'Accept'}
            onClick={onAcceptTimerValue}
            useDelay={false}
        />
    </>;

    // For performance reasons, we pre-create the Edit button
    const EDIT = <IconButton
        id={`${task.id}-edit-timer`}
        type={Icons.EDIT}
        size={Icons.IconSizeNormal}
        title={'Manually Edit Timer'}
        onClick={onEditTimer}
        useDelay={true}
    />;

    return <div className='task-timer'>
        <div className={`task-timer__widget ${isEditingTimer ? 'task-timer--editing' : ''}`.trim()}>

            {/* Play/Pause Button */}
            {!task.completionDate && <IconButton
                id={`${task.id}-start-timer`}
                type={task.running ? Icons.PAUSE : Icons.PLAY}
                size={Icons.IconSizeLarge}
                disabled={amIAdmin ? false : isEditingTimer || !canStart}
                color={task.running ? 'red' : 'green'}
                title={task.running ? 'Pause Timer' : 'Start Timer'}
                onClick={task.running ? onPauseTimer : onStartTimer}
                useDelay={true}
            />}

            {/* Timer Input Fields */}
            {isEditingTimer ? <div className='task-timer__input'>
                <DurationEntry disabled={false}
                               doAutofocus={true}
                               onChange={onChangeTimerValue}
                               showSeconds={true}
                               value={durationValue}
                />
            </div> : <div className='task-timer__text'>{renderTimerText()}</div>
            }

            {/* Ok/Cancel Buttons */}
            {!task.running ? isEditingTimer ? CHECK : EDIT : null}
        </div>

        {/* Completion */}
        {!task.running && !isEditingTimer
         ? <div className='task-timer__timestamp'>

             <div className='task-timer__completed'>Completed</div>

             <Checkbox checked={task.completionDate !== null}
                       onClick={onCompletionToggled}
                       disabled={finalized}
                       name='Completed'
             />

             <div className="task-timer__completed-text">
                 {!!task.completionDate && finalized ? ' on ' + DateUtil.formatDateTime(task.completionDate, true)
                                                     : <DatePicker name='completionDate'
                                                                   disabled={finalized}
                                                                   defaultValue={task.completionDate}
                                                                   onChange={props.onChangeCompletionDate}
                                                                   useTime={true}
                  />
                 }
             </div>
         </div>
         : null
        }
    </div>;
}

export type TaskTimerProps = {
    // The task we are working on
    task: Task,

    // Callback when a valid timer value is accepted
    onAcceptTimerValue: (timerValue: number) => void,

    // Callback when completion date changed
    onChangeCompletionDate: (date: Date | null) => void,

    // Callback when changing edit state
    onEditStateChange: (hasChanged: boolean) => void,

    // Called when the timer is paused
    onPauseTimer: (e: SyntheticEvent) => void,

    // Called when the timer is started
    onStartTimer: (e: SyntheticEvent) => void,

    // Called when the task is completed
    onCompletionToggled: (e: SyntheticEvent, data?: any) => void,
}

export default TaskTimer;
